import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ParentSidebar from './ParentSidebar';
import TopBar from '../TopBar';
import ParentSectionDashboard from './ParentSectionDashboard';
import ParentSectionChildren from './ParentSectionChildren';
import ParentSectionProgress from './ParentSectionProgress';
import ParentSectionPayments from './ParentSectionPayments';
import ParentSectionCourses from './ParentSectionCourses';
import ParentSectionControls from './ParentSectionControls';
import ParentSectionNotifications from './ParentSectionNotifications';
import ParentSectionSupport from './ParentSectionSupport';
import ProfileSettings from '../tabs/ProfileSettings';
import AccountSettings from '../tabs/AccountSettings';
// Import UserDashboard to render its content within Parent Dashboard
import UserDashboard from '../user/UserDashboard';
import './ParentDashboard.css';
import './ParentSidebar.css';

const PARENT_DASHBOARD_SECTION_KEY = 'syllera-parent-dashboard-section';

const ParentDashboard = () => {
  const { t } = useTranslation('parent');
  const [activeSection, setActiveSection] = useState(() => {
    try {
      return sessionStorage.getItem(PARENT_DASHBOARD_SECTION_KEY) || 'dashboard';
    } catch {
      return 'dashboard';
    }
  });
  const [sectionKey, setSectionKey] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth <= 768) {
        const sidebar = document.querySelector('.parent-sidebar');
        const hamburger = document.querySelector('.mobile-menu-toggle');
        if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSectionKey((prev) => prev + 1);
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (!activeSection) return;
    try {
      sessionStorage.setItem(PARENT_DASHBOARD_SECTION_KEY, activeSection);
    } catch {
      // Ignore storage errors
    }
  }, [activeSection]);

  // Learning sections that should show UserDashboard content
  const learningSections = [
    'explore-courses',
    'my-courses',  // Maps to 'courses' in UserDashboard (My Courses section)
    'vocabulary',
    'practice',
    'course-library',
    'talk',
    'virtual-classroom',
    'live-learning',
    'assignments',
    'shop'
  ];
  
  // Map parent section IDs to UserDashboard section IDs
  const getMappedSection = (section) => {
    const sectionMap = {
      'my-courses': 'courses'  // Parent's 'my-courses' -> UserDashboard's 'courses'
    };
    return sectionMap[section] || section;
  };

  const renderSection = () => {
    // If it's a learning section, render UserDashboard content within Parent Dashboard layout
    if (learningSections.includes(activeSection)) {
      const mappedSection = getMappedSection(activeSection);
      return (
        <div 
          key={`learning-${activeSection}-${sectionKey}`} 
          className="parent-learning-content"
          style={{ 
            width: '100%', 
            height: '100%', 
            position: 'relative',
            overflow: 'auto'
          }}
        >
          {/* Render UserDashboard but hide its sidebar and TopBar */}
          <div style={{ 
            display: 'flex',
            width: '100%',
            height: '100%'
          }}>
            {/* Hide UserDashboard's sidebar by CSS */}
            <div style={{ width: '100%', marginLeft: 0 }}>
              <UserDashboard initialSection={mappedSection} hideSidebar={true} />
            </div>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'dashboard':
        return <ParentSectionDashboard key={`dashboard-${sectionKey}`} />;
      case 'children':
        return <ParentSectionChildren key={`children-${sectionKey}`} />;
      case 'progress':
        return <ParentSectionProgress key={`progress-${sectionKey}`} />;
      case 'payments':
        return <ParentSectionPayments key={`payments-${sectionKey}`} />;
      case 'courses':
        return <ParentSectionCourses key={`courses-${sectionKey}`} />;
      case 'controls':
        return <ParentSectionControls key={`controls-${sectionKey}`} />;
      case 'notifications':
        return <ParentSectionNotifications key={`notifications-${sectionKey}`} />;
      case 'support':
        return <ParentSectionSupport key={`support-${sectionKey}`} />;
      case 'profile-settings':
        return (
          <div className="parent-main-content" key={`profile-settings-wrapper-${sectionKey}`}>
            <div className="parent-content-header">
              <h1 className="parent-content-title">{t('sidebar.profileSettings')}</h1>
              <p className="parent-content-subtitle">{t('profileSettings.subtitle')}</p>
            </div>
            <ProfileSettings key={`profile-settings-${sectionKey}`} />
          </div>
        );
      case 'account-settings':
        return (
          <div className="parent-main-content" key={`account-settings-wrapper-${sectionKey}`}>
            <div className="parent-content-header">
              <h1 className="parent-content-title">{t('sidebar.accountSettings')}</h1>
              <p className="parent-content-subtitle">{t('accountSettings.subtitle')}</p>
            </div>
            <AccountSettings key={`account-settings-${sectionKey}`} />
          </div>
        );
      default:
        return <ParentSectionDashboard key={`dashboard-${sectionKey}`} />;
    }
  };

  // Always render Parent Dashboard with Parent Sidebar
  // Learning sections will render UserDashboard content within this layout
  return (
    <div className="parent-dashboard-container">
      {sidebarOpen && (
        <div
          className="parent-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <TopBar
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />
      <div className="parent-dashboard-layout">
        <ParentSidebar
          activeSection={activeSection}
          setActiveSection={handleSectionChange}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <div className="parent-main-content-wrapper">
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;

